import {transition, trigger, useAnimation} from '@angular/animations';
import {Component} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {PageEvent} from '@angular/material/paginator';
import {ContactType} from '@app/models/abstract-contact.model';
import {ContactModel} from '@app/models/contact.model';
import {PageSizeTables} from '@app/models/enums/page-size-tables.enum';
import {GenericSearchTableActionsDirective} from '@app/service-provider/util/generic-search-table-actions.directive';
import {GtAuthenticationService} from '@app/services/gt-authentication.service';
import {GtDirectoryService} from '@app/services/gt-directory.service';
import {GtPageSizeService} from '@app/services/gt-page-size.service';
import {fadeAnimation} from '@app/shared/animations/fade.animation';
import {ProfileComponent} from '@app/shared/profile/profile.component';
import {BuilderFactory} from '@app/shared/utils/builder.factory';
import {GtPhoneNumberUtil} from '@app/shared/utils/gt-phone-number.util';
import {environment} from '@environment';
import {DirectoryService, GetContactsResponse} from '@gt-api';
import {EndUserTableEntry} from './end-user-table-entry';
import {EndUserDialogData, EndUserEditorComponent} from '@app/service-provider/end-user-editor/end-user-editor.component';
import {Router} from '@angular/router';

interface EndUserTypeName {
  id: ContactType;
  name: string;
}

@Component({
  templateUrl: './end-user-search.component.html',
  styleUrls: ['./end-user-search.component.scss', '../util/generic-search-table-actions.directive.scss'],
  animations: [trigger('fadeIn', [transition('* => shown', [useAnimation(fadeAnimation)])])]
})
export class EndUserSearchComponent extends GenericSearchTableActionsDirective<EndUserTableEntry, ContactModel> {
  dataSourceColumns = environment.userSearchColumns;
  pageSize: number;
  endUserTypes: EndUserTypeName[] = [
    {id: ContactType.EndUser, name: 'contacts.type_end_user'},
    {id: ContactType.ExternalEndUser, name: 'contacts.type_external_end_user'},
    {id: ContactType.InternalContact, name: 'contacts.type_internal_contact'}
  ];

  private readonly defaultTableSize = 25;

  constructor(
    private authenticationService: GtAuthenticationService,
    private directoryService: DirectoryService,
    private gtDirectoryService: GtDirectoryService,
    private router: Router,
    private dialog: MatDialog,
    private pageSizeService: GtPageSizeService
  ) {
    super();

    this.pageSize = this.pageSizeService.getPageSize(PageSizeTables.EndUserSearchTable, this.defaultTableSize);
  }

  showProfile(entry: EndUserTableEntry): void {
    this.gtDirectoryService.getEndUser(entry.entry.oname, entry.entry.login).subscribe(contact => {
      const dialogRef = this.dialog.open(ProfileComponent, {minWidth: 500, data: contact});
      dialogRef.componentInstance.canEdit = true;
      dialogRef.componentInstance.back.subscribe(() => {
        dialogRef.close();
      });
    });
  }

  saveRow(entry: any): void {
    throw new Error('Method saveRow not implemented.');
  }

  newTableEntry(entry: ContactModel, editing: boolean): EndUserTableEntry {
    const builder = BuilderFactory.createBuilder<EndUserTableEntry>();
    builder
      .editing(editing)
      .number(GtPhoneNumberUtil.toDisplay(entry.phoneNumber))
      .name(entry.name)
      .organization(entry.ownerOrganization)
      .shortNumber(entry.shortNumber)
      .type(entry.type)
      .entry(entry);
    return builder.build();
  }

  search(searchString: string) {
    this.startLoading();
    this.directoryService.searchEndUsers1(this.authenticationService.oname, this.authenticationService.login, searchString).subscribe(
      (response: GetContactsResponse) => {
        if (this.stopLoading()) {
          const data = response.code === 200 ? response.contactList : [];
          this.dataSource.data = data.map(e => this.newTableEntry(ContactModel.convertContactModel(e), false));
        }
      },
      () => this.stopLoading()
    );
  }

  getContactTypeDisplayName(type: ContactType): string {
    const endUserType = this.endUserTypes.find(membType => membType.id === type);
    if (endUserType) {
      return endUserType.name;
    }
    return type.toString();
  }

  paging(pageEvent: PageEvent) {
    this.pageSizeService.setPageSize(PageSizeTables.EndUserSearchTable, pageEvent.pageSize);
  }

  hasEditEndUserRights(): boolean {
    return this.authenticationService.endUserAccessRights.edit;
  }

  editRow(entry: EndUserTableEntry) {
    if (this.hasEditEndUserRights()) {
      const dialogData: EndUserDialogData = {
        endUser: {...entry.entry.backendModel},
        editMode: true
      };

      this.openDialog(dialogData);
    }
  }

  openDialog(dialogData: EndUserDialogData) {
    const dialogRef = this.dialog.open(EndUserEditorComponent, {
      minWidth: 600,
      disableClose: true,
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(updateSearch => {
      if (updateSearch) {
        this.search(dialogRef.componentInstance.endUser.fullNumber);
      }
    });
  }

  showRow(entry: EndUserTableEntry) {
    this.router.navigate(['admin', 'end-users'], {
      queryParams: {contactOname: entry.entry.oname}
    });
  }
}
